/* eslint-disable max-lines */
// types
import { DeliveryAddress, ANNOTATION_ASSETS } from "@swiggy-private/payment-types";
import { ADDRESS_CATEGORIES } from "@swiggy-private/core-types";
import {
    FetchTransitionLottie,
    GetTickScreenLottie,
    LottieMarker,
    OnTransitionError,
    SuperMessage,
    TickScreenProps,
    TRANSITION_ERROR_TYPES,
    PostSuccessDelayOptions,
    TickScreenTransitionLottieConfig,
} from "../interfaces";

// helpers
import { getCloudinaryUrl, isEmpty } from "@swiggy-private/js-utils";
import {
    captureError,
    FESTIVE_END_DATE,
    FESTIVE_START_DATE,
    isDateWithinInterval,
    replaceText,
} from "@swiggy-private/core-ui";
import {
    fetchTransitionMROStripLottie,
    prefetchTransitionMROStripLottie,
    updateDynamicValuesInMROLottieData,
} from "./mroLottie";

// constants
import {
    REWARD_UNLOCKED_WITH_SAVINGS,
    REWARD_UNLOCKED_WITHOUT_SAVINGS,
    TRANSITION_LOTTIE_ASSET_PATH,
    TRANSITION_LOTTIE_BLACK_URL,
    TRANSITION_LOTTIE_POP_URL,
    TRANSITION_LOTTIE_URL,
    FESTIVE_TRANSITION_LOTTIE_URL,
    TRANSITION_LOTTIE_BLACK_OTP_URL,
    BLACK_OTP_LOTTIE_ASSET_IDS,
    TRANSITION_LOTTIE_BOLT_URL,
    TRANSITION_LOTTIE_BOLT_OTP_URL,
    blackBenefitTextTimeout,
    BOLT_TIMEOUT,
    GIFTING_CART_TIMEOUT,
    TRANSITION_LOTTIE_AFFORDABLE_URL,
    AFFORDABLE_CART_M2_TIMEOUT,
    TRANSITION_LOTTIE_GIFTING_ORDER_PLACED_URL,
    TRANSITION_LOTTIE_MRO_URL,
    TRANSITION_MRO_STRIP_LOTTIE_URL,
    MRO_TIMEOUT,
    ON_SUCCESS_CALLBACK_TYPE,
} from "../constants";

export { fetchTransitionMROStripLottie, prefetchTransitionMROStripLottie, updateDynamicValuesInMROLottieData };

export const shouldShowTickScreenV5 = ({
    isRegular,
    isGiftingCart,
    isMroExperienceEnabledOnOrderConfirmationScreen,
    tickScreenLottieUrl,
}: TickScreenProps): boolean =>
    Boolean(isRegular || isGiftingCart || tickScreenLottieUrl || isMroExperienceEnabledOnOrderConfirmationScreen);

export const getSavingsText = (savings?: SuperMessage, isMilestoneUnlockEligible?: boolean) => {
    if (isEmpty(savings)) {
        return isMilestoneUnlockEligible ? replaceText(REWARD_UNLOCKED_WITHOUT_SAVINGS) : "";
    }

    const message = savings?.successMessageInfo || savings?.successMessage;

    if (isEmpty(message)) {
        return isMilestoneUnlockEligible ? replaceText(REWARD_UNLOCKED_WITHOUT_SAVINGS) : "";
    }

    if (isMilestoneUnlockEligible) {
        return replaceText(`${message} ${REWARD_UNLOCKED_WITH_SAVINGS}`);
    }

    return replaceText(message);
};

export const hasInvalidAddressCategory = (address?: DeliveryAddress | null): boolean => {
    return (
        isEmpty(address?.userIntentAddressCategory) ||
        address?.userIntentAddressCategory === ADDRESS_CATEGORIES.ADDRESS_CATEGORY_INVALID
    );
};

export const getAddressAnotationAsset = (address?: DeliveryAddress, category?: number): ANNOTATION_ASSETS => {
    switch (category) {
        case ADDRESS_CATEGORIES.ADDRESS_CATEGORY_HOME:
            return ANNOTATION_ASSETS.HOME;
        case ADDRESS_CATEGORIES.ADDRESS_CATEGORY_WORK:
            return ANNOTATION_ASSETS.WORK;
        case ADDRESS_CATEGORIES.ADDRESS_CATEGORY_FRIENDS_AND_FAMILY:
            return ANNOTATION_ASSETS.HOME;
        case ADDRESS_CATEGORIES.ADDRESS_CATEGORY_OTHER:
        default:
            return ANNOTATION_ASSETS.OTHER;
    }
};

export const fetchTransitionAsset = async (id: string, onError: OnTransitionError): Promise<string | null> => {
    if (isEmpty(id)) {
        onError(TRANSITION_ERROR_TYPES.ASSET_FETCH_ERROR, "Invalid asset id");
        return null;
    }

    const url = getCloudinaryUrl(id);

    try {
        if (isEmpty(url)) {
            onError(TRANSITION_ERROR_TYPES.ASSET_FETCH_ERROR, "Invalid asset url");
            return null;
        }

        const response = await fetch(url);
        const blob = await response.blob();
        return URL?.createObjectURL?.(blob);
    } catch (error) {
        onError(TRANSITION_ERROR_TYPES.ASSET_FETCH_ERROR, error, url);
        return null;
    }
};

export function getTickScreenTransitionLottieUrl({
    isSwiggyBlackApplicable,
    isSwiggyBlackOtpAnimationApplicable,
    isPopCart,
    isBoltCartM3,
    isAffordableCartM2,
    isGiftingCart,
    isMroFlow,
}: GetTickScreenLottie): TickScreenTransitionLottieConfig {
    const isFestivePeriod = isDateWithinInterval(new Date(), FESTIVE_START_DATE, FESTIVE_END_DATE);
    let lottieUrl: string | undefined = TRANSITION_LOTTIE_URL;
    let onSuccessCallbackType = ON_SUCCESS_CALLBACK_TYPE.INVALID;

    if (isGiftingCart) {
        lottieUrl = TRANSITION_LOTTIE_GIFTING_ORDER_PLACED_URL;
    } else if (isSwiggyBlackApplicable) {
        if (isSwiggyBlackOtpAnimationApplicable && !isBoltCartM3) {
            lottieUrl = TRANSITION_LOTTIE_BLACK_OTP_URL;
        } else if (isBoltCartM3 && !isSwiggyBlackOtpAnimationApplicable) {
            lottieUrl = TRANSITION_LOTTIE_BOLT_URL;
        } else if (isBoltCartM3 && isSwiggyBlackOtpAnimationApplicable) {
            lottieUrl = TRANSITION_LOTTIE_BOLT_OTP_URL;
        } else if (isAffordableCartM2) {
            lottieUrl = TRANSITION_LOTTIE_AFFORDABLE_URL;
        } else {
            lottieUrl = TRANSITION_LOTTIE_BLACK_URL;
        }
    } else if (isPopCart) {
        lottieUrl = TRANSITION_LOTTIE_POP_URL;
    } else if (isFestivePeriod) {
        lottieUrl = FESTIVE_TRANSITION_LOTTIE_URL;
    } else if (isBoltCartM3) {
        lottieUrl = TRANSITION_LOTTIE_BOLT_URL; // bolt lottie
    } else if (isAffordableCartM2) {
        lottieUrl = TRANSITION_LOTTIE_AFFORDABLE_URL; // 99 store lottie
    } else if (isMroFlow) {
        lottieUrl = TRANSITION_LOTTIE_MRO_URL;
        onSuccessCallbackType = ON_SUCCESS_CALLBACK_TYPE.MRO;
    }

    return {
        lottieUrl,
        onSuccessCallbackType,
    };
}

const isNumericLayerValue = (value: string): boolean => /^\d+(\.\d+)?$/.test(value.trim());
const applyLayerValue = (existing: string, incoming: string): string => {
    if (incoming === "") {
        return "";
    }
    if (!existing) {
        return incoming;
    }
    if (/\d+/.test(existing) && isNumericLayerValue(incoming)) {
        console.log("inside");
        return existing.replace(/\d+/, incoming.trim());
    }
    return incoming;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const applyTransitionLottieLayerText = (layers: any[] | undefined, layerText?: Record<string, string>): void => {
    if (!layers?.length || !layerText) {
        return;
    }

    layers.forEach((layer) => {
        if (!(layer.nm in layerText) || !layer.t?.d?.k) {
            return;
        }
        const incoming = layerText[layer.nm] ?? "";

        layer.t.d.k.forEach((keyframe: any) => {
            if (keyframe.s?.t != null) {
                console.log("keyframe", keyframe.s.t);
                keyframe.s.t = applyLayerValue(String(keyframe.s.t), incoming);
            }
        });
    });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const updateTransitionLottieLayerText = (lottieData: any, layerText?: Record<string, string>): void => {
    if (!lottieData || !layerText) {
        return;
    }
    applyTransitionLottieLayerText(lottieData.layers, layerText);
};

export const fetchTransitionLottie = async ({
    onError,
    deliveryAddressAnotation,
    isSwiggyBlackApplicable,
    isSwiggyBlackOtpAnimationApplicable,
    isPopCart,
    isBoltCartM3,
    isAffordableCartM2,
    isGiftingCart,
    tickScreenLottieUrl,
    transitionLottieConfig,
    isMroFlow,
    childOrdersCount,
}: FetchTransitionLottie): Promise<unknown> => {
    const { lottieUrl: internalTransitionLottie = "", onSuccessCallbackType } = getTickScreenTransitionLottieUrl({
        isSwiggyBlackApplicable,
        isSwiggyBlackOtpAnimationApplicable,
        isPopCart,
        isBoltCartM3,
        isAffordableCartM2,
        isGiftingCart,
        isMroFlow,
    });

    const transitionLottie = transitionLottieConfig?.url
        ? transitionLottieConfig.url
        : (tickScreenLottieUrl ?? internalTransitionLottie);

    // Warm strip cache only when MRO actually wins lottie selection (not festive/bolt/etc.).
    const isMroSuccessCallbackType = onSuccessCallbackType === ON_SUCCESS_CALLBACK_TYPE.MRO;
    if (isMroSuccessCallbackType) {
        prefetchTransitionMROStripLottie(TRANSITION_MRO_STRIP_LOTTIE_URL);
    }

    try {
        const isBlckOTPCase = isSwiggyBlackApplicable && isSwiggyBlackOtpAnimationApplicable;
        const lottieRequest = await fetch(transitionLottie);
        let lottieData = await lottieRequest.json();

        const staticAsset = await fetchTransitionAsset(
            `${TRANSITION_LOTTIE_ASSET_PATH}/${ANNOTATION_ASSETS.STATIC}`,
            onError,
        );

        // update the delivery address annotation
        if (deliveryAddressAnotation && lottieData?.assets?.[0]?.p && lottieData?.assets?.[0]?.u) {
            const addressAsset = await fetchTransitionAsset(
                `${TRANSITION_LOTTIE_ASSET_PATH}/${deliveryAddressAnotation}`,
                onError,
            );
            lottieData.assets[0].p = addressAsset;
            lottieData.assets[0].u = "";
        }

        // update the static asset
        if (staticAsset && lottieData?.assets?.[1]?.p && lottieData?.assets?.[1]?.u) {
            lottieData.assets[1].p = staticAsset;
            lottieData.assets[1].u = "";
        }
        // updates for tickscreen transition custom lottie
        updateTransitionLottieLayerText(lottieData, transitionLottieConfig?.layerText);

        // updates for blck assets
        // [!IMPORTANT] Adding `isBlckOTPCase` condition because there might be cases when lottie has assets at
        // [!IMPORTANT] these positions but we need to update with these assets only when it is BLCK + OTP case
        if (isBlckOTPCase) {
            if (lottieData?.assets?.[2]?.p && lottieData?.assets?.[2]?.u) {
                const starAsset = await fetchTransitionAsset(
                    `${TRANSITION_LOTTIE_ASSET_PATH}/${BLACK_OTP_LOTTIE_ASSET_IDS.STAR}`,
                    onError,
                );
                lottieData.assets[2].p = starAsset;
                lottieData.assets[2].u = "";
            }

            if (lottieData?.assets?.[3]?.p && lottieData?.assets?.[3]?.u) {
                const shimmerAsset = await fetchTransitionAsset(
                    `${TRANSITION_LOTTIE_ASSET_PATH}/${BLACK_OTP_LOTTIE_ASSET_IDS.SHIMMER}`,
                    onError,
                );
                lottieData.assets[3].p = shimmerAsset;
                lottieData.assets[3].u = "";
            }

            if (lottieData?.assets?.[4]?.p && lottieData?.assets?.[4]?.u) {
                const otpStampAsset = await fetchTransitionAsset(
                    `${TRANSITION_LOTTIE_ASSET_PATH}/${BLACK_OTP_LOTTIE_ASSET_IDS.OTP_STAMP}`,
                    onError,
                );
                lottieData.assets[4].p = otpStampAsset;
                lottieData.assets[4].u = "";
            }
        }

        if (isMroFlow && isMroSuccessCallbackType) {
            lottieData = updateDynamicValuesInMROLottieData(lottieData, childOrdersCount);
        }

        return lottieData;
    } catch (e) {
        captureError(e);
        onError(TRANSITION_ERROR_TYPES.FETCH_FAILED, e, transitionLottie);
    }
};

// can't type lottieData
export const getLottieSoundMarker = (lottieData: any, markerName: string) => {
    if (!isEmpty(lottieData) && !isEmpty(lottieData.markers)) {
        return lottieData.markers.find((marker: LottieMarker) => marker.cm === markerName);
    }
};

export const getPostSuccessDelay = ({
    isSwiggyBlackApplicable = false,
    blackBenefitsTimeout,
    successTimeout,
    isBoltCartM3 = false,
    isAffordableCartM2 = false,
    isGiftingCart = false,
    transitionLottieConfig,
    isMroFlow = false,
}: PostSuccessDelayOptions): number => {
    if (isGiftingCart) {
        return GIFTING_CART_TIMEOUT;
    }

    if (isBoltCartM3) {
        return BOLT_TIMEOUT;
    }

    if (isAffordableCartM2) {
        return AFFORDABLE_CART_M2_TIMEOUT;
    }

    if (isMroFlow) {
        return MRO_TIMEOUT;
    }

    if (transitionLottieConfig?.delay != null) {
        return transitionLottieConfig.delay;
    }

    return isSwiggyBlackApplicable ? blackBenefitTextTimeout(blackBenefitsTimeout) : successTimeout;
};
